import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import MainVideo from '../components/courses/MainVideo';
import { useTranslation } from 'react-i18next';
import NextCoursesFromPath from '../components/courses/NextCoursesFromPath';
import SimilarCoursesByProfessions from '../components/courses/SimilarCoursesByProfessions';
import HeroSection from '../components/courses/HeroSection';
import ProgressSection from '../components/courses/ProgressSection';
import ReviewsSection from '../components/courses/ReviewsSection';
import LessonsSection from '../components/courses/LessonsSection';
import ModulesSection from '../components/courses/ModulesSection';
import { APP_URL } from '../config';

const serverUrl = APP_URL;

// Utility function to format duration from minutes to hours and minutes
const formatDuration = (minutes, t) => {
  if (!minutes) return 'N/A';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    return `${remainingMinutes} ${t('common:time.shortMin')}`;
  } else if (remainingMinutes === 0) {
    return `${hours} ${t('common:time.shortHour')}`;
  } else {
    return `${hours} ${t('common:time.shortHour')} ${remainingMinutes} ${t(
      'common:time.shortMin'
    )}`;
  }
};

const scrollToModule = (moduleIndex) => {
  const moduleElement = document.getElementById(`module-${moduleIndex}`);
  if (!moduleElement) return;

  const headerOffset = 60;
  const elementPosition = moduleElement.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });

  // Update URL hash
  const newHash = `module-${moduleIndex}`;
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}#${newHash}`
  );
};

const CourseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation(['courses', 'common']);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProgress, setUserProgress] = useState(0);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const calculateCourseStats = useCallback((courseData) => {
    return {
      lessonCount: courseData?.lessonCount || 0,
      testCount: courseData?.testCount || 0,
      totalStudents: courseData?.enrolledStudents?.length || 0,
      duration: courseData?.duration || 0,
      videoContent: Array.isArray(courseData?.videoContent) ? courseData.videoContent : [],
      presentationCount: courseData?.presentationCount || 0,
    };
  }, []);

  const [courseStats, setCourseStats] = useState({
    lessonCount: 0,
    testCount: 0,
    totalStudents: 0,
    duration: 0,
    videoContent: [],
    presentationCount: 0,
  });

  const fetchCourseDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await coursesAPI.getBySlugWithProfessions(
        slug,
        isAuthenticated ? { userId: user._id } : {}
      );
      const courseData = response.data;
      // Собираем отладочную информацию о тестах (теперь они в уроках)
      const allTests = [];
      courseData.modules?.forEach((moduleRef) => {
        const module = moduleRef.module || moduleRef; // Поддержка старой структуры
        if (module.lessons && module.lessons.length > 0) {
          module.lessons.forEach((lesson) => {
            if (lesson.tests && lesson.tests.length > 0) {
              lesson.tests.forEach((test) => {
                allTests.push({
                  id: test._id,
                  title: test.title,
                  lessonTitle: lesson.title,
                });
              });
            }
          });
        }
      });

      // Используем данные о прогрессе пользователя из ответа API
      if (isAuthenticated && courseData.userProgress) {
        const { completedLessons = [] } = courseData.userProgress;

        // Добавляем информацию о завершении к каждому уроку
        if (courseData.modules && Array.isArray(courseData.modules)) {
          courseData.modules = courseData.modules.map((moduleRef) => {
            const module = moduleRef.module || moduleRef; // Поддержка старой структуры
            if (module.lessons) {
              module.lessons = module.lessons.map((lesson) => {
                const isCompleted = completedLessons.includes(lesson._id);
                return { ...lesson, completed: isCompleted };
              });
            }
            return moduleRef; // Возвращаем исходную структуру
          });
        }

        // Устанавливаем прогресс пользователя
        setUserProgress(courseData.userProgress.progress || 0);
      }

      setCourse(courseData);

      if (courseData.modules && courseData.modules.length > 0) {
        const firstIncompleteModule = courseData.modules.find((moduleRef) => {
          const module = moduleRef.module || moduleRef;
          return !module.completed;
        });
        const selectedModule = firstIncompleteModule || courseData.modules[0];
        setSelectedModule(selectedModule?.module || selectedModule);
      }

      // Вычисляем статистику курса
      const stats = calculateCourseStats(courseData);
      setCourseStats(stats);

      // Проверяем статус записи на курс
      if (isAuthenticated) {
        try {
          // Используем специальный эндпоинт для проверки статуса записи
          const enrollmentResponse = await coursesAPI.checkEnrollment(
            courseData._id
          );
          setIsEnrolled(enrollmentResponse.data.isEnrolled);
          console.log(
            'Enrollment status check:',
            enrollmentResponse.data.isEnrolled
          );
        } catch (enrollmentError) {
          console.error('Error checking enrollment status:', enrollmentError);
          // В случае ошибки, сбрасываем статус
          setIsEnrolled(false);
        }
      } else {
        setIsEnrolled(false);
      }

      if (isAuthenticated && courseData.userRating !== undefined) {
        setUserRating(courseData.userRating);
      } else {
        setUserRating(null);
      }

      setIsBookmarked(!!courseData.bookmarked);
    } catch (err) {
      setError(t('courses:courseDetail.errors.loadError'));
      console.error('Ошибка при загрузке курса:', err);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated, user?._id, calculateCourseStats, t]);

  useEffect(() => {
    fetchCourseDetails();
  }, [fetchCourseDetails]);

  const handleModuleSelect = useCallback((module) => {
    setSelectedModule(module);
  }, []);

  useEffect(() => {
    if (!course) return;

    const hash = window.location.hash;
    if (hash && hash.startsWith('#module-')) {
      const moduleIndex = parseInt(hash.replace('#module-', ''));
      if (
        !isNaN(moduleIndex) &&
        moduleIndex >= 0 &&
        moduleIndex < course.modules.length
      ) {
        setTimeout(() => {
          scrollToModule(moduleIndex);

          // Якщо це останній модуль — доскролюємо ще раз пізніше
          if (moduleIndex === course.modules.length - 1) {
            setTimeout(() => scrollToModule(moduleIndex), 500);
          }
        }, 100);
      } else {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [course]);

  // Проверка, имеет ли пользователь права админа или учителя
  const isAdmin = user?.userMeta.roles.includes('admin');
  const isTeacher = user?.userMeta.roles.includes('teacher');
  const hasSpecialAccess = isAdmin || isTeacher;

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      toast.info(t('courses:courseDetail.toasts.loginToEnroll'));
      navigate('/login');
      return;
    }

    try {
      console.log('Attempting to enroll:', {
        courseId: course._id,
        userId: user._id,
      });

      await coursesAPI.enroll(course._id);

      // Обновляем статус записи
      setIsEnrolled(true);

      // Показываем сообщение об успехе
      toast.success(t('courses:courseDetail.enrollSuccess'));

      // Перезагружаем данные курса для обновления статистики студентов
      fetchCourseDetails();
    } catch (err) {
      // Проверяем, не связана ли ошибка с тем, что пользователь уже записан
      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes('уже записаны')
      ) {
        setIsEnrolled(true);
        toast.info(t('courses:courseDetail.toasts.alreadyEnrolled'));
        // Также обновляем данные при повторной попытке записи
        fetchCourseDetails();
      } else {
        console.error('Enrollment error:', err);
        toast.error(
          err.response?.data?.message ||
            t('courses:courseDetail.toasts.enrollError')
        );
      }
    }
  };

  const handleRatingSubmit = async (ratingData) => {
    // Update user rating state
    setUserRating(ratingData.rating);

    // Refresh course data to get updated rating and count
    setTimeout(() => {
      fetchCourseDetails();
    }, 500);
  };

  const handleStartLesson = (moduleIndex, lessonIndex) => {
    if (!isAuthenticated) {
      // Показываем уведомление для неавторизованных пользователей
      toast.info(t('courses:courseDetail.toasts.loginToEnroll'));
      return;
    }

    if (!isEnrolled && !isAdmin) {
      toast.info(t('courses:courseDetail.toasts.lessonEnrollRequired'));
      return;
    }

    if (
      course &&
      course.modules &&
      course.modules[moduleIndex] &&
      (course.modules[moduleIndex].module || course.modules[moduleIndex])
        .lessons &&
      (course.modules[moduleIndex].module || course.modules[moduleIndex])
        .lessons[lessonIndex]
    ) {
      const module =
        course.modules[moduleIndex].module || course.modules[moduleIndex];
      const moduleSlug = module.slug || module._id;
      const lesson = module.lessons[lessonIndex];
      const lessonSlug = lesson.slug || lesson._id;

      // Переходим по URL к уроку
      navigate(
        `/courses/${course.slug}/modules/${moduleSlug}/lessons/${lessonSlug}`
      );
    }
  };

  const handleStartTest = (testId, moduleSlug, lessonSlug) => {
    if (!isAuthenticated) {
      toast.info(t('courses:courseDetail.toasts.testLoginRequired'));
      navigate('/login');
      return;
    }

    if (!isEnrolled) {
      toast.info(t('courses:courseDetail.toasts.testEnrollRequired'));
      return;
    }

    const path = lessonSlug
      ? `/courses/${slug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${testId}`
      : `/courses/${slug}/modules/${moduleSlug}/tests/${testId}`;
    navigate(path);
  };

  const handleViewTest = (testId, moduleSlug) => {
    navigate(`/courses/${slug}/modules/${moduleSlug}/tests/${testId}/preview`);
  };

  const navigateToFirstUncompleted = useCallback(() => {
    if (!course || !course.modules) return;

    for (const moduleRef of course.modules) {
      const module = moduleRef.module || moduleRef; // Поддержка старой структуры
      if (module.lessons) {
        for (const lesson of module.lessons) {
          if (!lesson.completed) {
            navigate(
              `/courses/${slug}/modules/${module.slug || module._id}/lessons/${
                lesson.slug || lesson._id
              }`
            );
            return;
          }

          // Проверяем тесты урока
          if (lesson.tests && lesson.tests.length > 0) {
            for (const test of lesson.tests) {
              const testId =
                typeof test._id === 'string' ? test._id : test._id.toString();
              if (!course.userProgress?.completedTests?.includes(testId)) {
                const lessonSlug = lesson.slug || lesson._id;
                navigate(
                  `/courses/${slug}/modules/${
                    module.slug || module._id
                  }/lessons/${lessonSlug}/tests/${testId}`
                );
                return;
              }
            }
          }
        }
      }
    }

    // If all lessons and tests are completed, navigate to the first lesson
    const firstModuleRef = course.modules[0];
    if (firstModuleRef) {
      const firstModule = firstModuleRef.module || firstModuleRef;
      if (
        firstModule &&
        firstModule.lessons &&
        firstModule.lessons.length > 0
      ) {
        const firstLesson = firstModule.lessons[0];
        navigate(
          `/courses/${slug}/modules/${
            firstModule.slug || firstModule._id
          }/lessons/${firstLesson.slug || firstLesson._id}`
        );
      }
    }
  }, [course, navigate, slug]);

  // Проверка, может ли пользователь редактировать курс
  const canEdit = useMemo(() => {
    if (!user || !course) return false;

    // Админ может редактировать любой курс
    if (user.userMeta.roles.includes('admin')) return true;

    // Учитель может редактировать любой курс
    if (user.userMeta.roles.includes('teacher')) return true;

    return (
      course.instructor && course.instructor._id === user._id && course.isDraft
    );
  }, [user, course]);

  // Обработчик удаления курса
  const handleDeleteCourse = async () => {
    try {
      await coursesAPI.delete(course._id);
      toast.success(t('courses:courseDetail.toasts.courseDeleted'));
      navigate('/courses');
    } catch (error) {
      console.error('Ошибка при удалении курса:', error);
      toast.error(
        error.response?.data?.message ||
          t('courses:courseDetail.toasts.courseDeleteError')
      );
    }
  };

  const handleToggleBookmark = async () => {
    try {
      const response = await coursesAPI.toggleBookmark(course._id);
      setIsBookmarked(response.data.bookmarked);
      toast.success(
        response.data.bookmarked
          ? t('courses:courseDetail.toasts.bookmarkAdded')
          : t('courses:courseDetail.toasts.bookmarkRemoved')
      );
    } catch (error) {
      console.error('Ошибка при управлении закладкой:', error);
      toast.error(t('courses:courseDetail.toasts.bookmarkError'));
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={fetchCourseDetails}
          >
            {t('common:actions.retry')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8 }}>
          <Alert severity="warning">
            {t('courses:courseDetail.errors.notFound')}
          </Alert>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/courses')}
          >
            {t('courses:courseDetail.errors.backToCourses')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4 }}>
        {/* Action buttons row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          {/* Breadcrumb */}
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: 'text.secondary',
              },
            }}
          >
            <Link
              color="text.secondary"
              href="/courses"
              onClick={(e) => {
                e.preventDefault();
                navigate('/courses');
              }}
              sx={{
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'text.primary',
                },
              }}
            >
              {t('common:breadcrumb.courses')}
            </Link>
            <Typography
              color="text.primary"
              sx={{
                fontWeight: 600,
              }}
            >
              {course?.title}
            </Typography>
          </Breadcrumbs>

          {/* Edit/Delete Buttons */}
          {canEdit && (
            <Box
              sx={{ display: 'flex', gap: 2 }}
              className="course-edit-delete-buttons"
            >
              <Button
                onClick={() => navigate(`/courses/${slug}/edit`)}
                sx={{
                  minWidth: { xs: 'unset', sm: 'auto' },
                }}
              >
                <EditIcon sx={{ mr: { xs: 0, sm: 1 } }} />
                <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {t('common:actions.edit')}
                </Typography>
              </Button>
              <Button
                color="error"
                onClick={() => setShowDeleteConfirm(true)}
                sx={{
                  minWidth: { xs: 'unset', sm: 'auto' },
                }}
              >
                <DeleteIcon sx={{ mr: { xs: 0, sm: 1 } }} />
                <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {t('common:actions.delete')}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
        {/* Диалог подтверждения удаления */}
        <Dialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: '12px',
              p: 2,
            },
          }}
        >
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <DeleteIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                mb: 1,
                fontSize: '1.25rem',
                fontWeight: 600,
              }}
            >
              {t('courses:courseDetail.deleteConfirm.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('courses:courseDetail.deleteConfirm.message')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outlined"
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                onClick={handleDeleteCourse}
                variant="contained"
                color="error"
                autoFocus
              >
                {t('common:actions.delete')}
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Hero Section - Full Width */}
        <HeroSection
          course={course}
          courseStats={courseStats}
          isEnrolled={isEnrolled}
          isBookmarked={isBookmarked}
          onToggleBookmark={handleToggleBookmark}
          onContinueLearning={() => navigateToFirstUncompleted()}
          onEnroll={handleEnroll}
          formatDuration={formatDuration}
          onScrollToReviews={() => {
            const reviewsSection = document.getElementById('reviews-section');
            if (reviewsSection) {
              reviewsSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          userRating={userRating}
          onRatingSubmit={handleRatingSubmit}
        />

        {/* Progress Section - Only show for enrolled users */}
        {isEnrolled && (
          <ProgressSection
            userProgress={userProgress}
            completedLessons={
              course?.modules?.reduce((total, moduleRef) => {
                const module = moduleRef.module || moduleRef;
                if (module.lessons) {
                  return (
                    total +
                    module.lessons.filter((lesson) => lesson.completed).length
                  );
                }
                return total;
              }, 0) || 0
            }
            totalLessons={courseStats?.lessonCount || 0}
          />
        )}

        <Grid container spacing={3}>
          {/* Левая колонка с информацией о курсе */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ mb: 4 }}>
              {/* Main Course Video */}
              {course.videos && course.videos.length > 0 && (
                <Box sx={{ position: 'relative', margin: '0 auto 20px' }}>
                  <MainVideo
                    videoUrl={
                      course.videos[0].startsWith('http')
                        ? course.videos[0]
                        : `${serverUrl}${course.videos[0]}`
                    }
                  />
                </Box>
              )}
            </Box>

            {/* Modules Section with Video Shorts */}
            {course.modules && course.modules.length > 0 && (
              <ModulesSection
                modules={course.modules}
                isEnrolled={isEnrolled}
                hasSpecialAccess={hasSpecialAccess}
                selectedModule={selectedModule}
                onModuleSelect={handleModuleSelect}
              />
            )}

            {/* Lessons Section */}
            {course && course.modules && course.modules.length > 0 && selectedModule && (
              <LessonsSection
                module={selectedModule}
                isEnrolled={isEnrolled}
                userProgress={course.userProgress}
                hasSpecialAccess={hasSpecialAccess}
                onStartLesson={(lessonIndex) => {
                  if (selectedModule) {
                    const moduleIndex = course.modules.findIndex(
                      (moduleRef) => {
                        const module = moduleRef.module || moduleRef;
                        const selectedModuleData =
                          selectedModule.module || selectedModule;
                        return module._id === selectedModuleData._id;
                      }
                    );

                    if (moduleIndex !== -1) {
                      handleStartLesson(moduleIndex, lessonIndex);
                    }
                  }
                }}
                onStartTest={handleStartTest}
                onViewTest={handleViewTest}
                className="course-modules"
              />
            )}

            {course && (
              <ReviewsSection
                course={course}
                isEnrolled={isEnrolled}
                userRating={userRating}
                compact={true}
                onRatingChange={setUserRating}
              />
            )}
          </Grid>

          {/* Правая колонка с информацией о прогрессе и записи */}
          <Grid item xs={12} lg={4}>
            <Box className="course-right-column">
              {/* Следующие курсы из активной дорожки обучения */}
              {user && <NextCoursesFromPath />}

              {/* Похожие курсы по профессиям */}
              {course && <SimilarCoursesByProfessions courseId={course._id} />}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default CourseDetail;
